/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import { convertAccessLevelForFrontend } from '../components/Misc/utils';
import {
  fetchUserDetails,
  fetchUserConfigs,
  fetchSharedConfigs,
  fetchInjector,
} from '../components/Misc/apiUtils';
// core components
import Header from 'components/Headers/Header';
import { GlobalContext } from '../api/GlobalContext';
import {
  TemplateRowName,
  SharedTemplateRowName,
} from '../components/Misc/TemplateRowName';
import Modal from '../components/Modals/Modal.js';
import { getHaveInjector } from '../EMR_Integration/react_webInjector.ts';

const Templates = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get our Globals
  const isCX = window.location.protocol === 'chrome-extension:';
  const { apiClient } = useContext(GlobalContext);

  // Update the user patients and conversations the first time this component loads
  const [templates, setTemplates] = useState([]);
  const [sharedtemplates, setSharedTemplates] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [isSharedTemplates, setIsSharedTemplates] = useState(false);
  const [isEmrTemplates, setIsEmrTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState(isCX ? 'emr' : 'personal');
  const [userEmail, setUserEmail] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalButtons, setModalButtons] = useState('');
  const [hideEmrTab, setHideEmrTab] = useState(true);
  const [haveInjector, setHaveInjector] = useState(getHaveInjector());

  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get('tab') || (isCX ? 'emr' : 'personal'); // Use the tab from the URL

  // Use currentTab to set active tab
  useEffect(() => {
    toggleTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    fetchUserDetails(apiClient)
      .then((userDetails) => {
        // Extract user email from user details
        setDefaultTemplate(userDetails.defaultConfig || '');
        setUserEmail(userDetails.email || '');

        // Then use the user email to fetch user configs and user shared configs
        Promise.all([
          fetchUserConfigs(apiClient),
          fetchSharedConfigs(apiClient, userEmail),
        ])
          .then(([userConfigsData, sharedConfigsData]) => {
            // Process user configs
            // for config in data[configs: add a entry for the formated last used date new Date(template.lastUsed + "Z")
            if (userConfigsData && userConfigsData.length > 0) {
              for (let i = 0; i < userConfigsData.length; i++) {
                userConfigsData[i]['lastUsed'] = new Date(
                  userConfigsData[i]['lastUsed'] + 'Z',
                ).toLocaleString();
              }
              // Sort the conversations by last used
              userConfigsData.sort((a, b) => {
                return new Date(b.lastUsed) - new Date(a.lastUsed);
              });

              setTemplates(userConfigsData);
              let emrTemplates = userConfigsData.filter(
                (template) =>
                  template.emr_url !== null &&
                  !template.emr_url.includes('app.comprehendpt.com'),
              );
              if (emrTemplates && emrTemplates.length === 0) {
                setHideEmrTab(true);
              } else {
                setHideEmrTab(false);
              }
            }
            // Process user shared configs
            // for config in data[sharedConfigs: add a entry for the formated last used date new Date(template.lastUsed + "Z")
            if (sharedConfigsData && sharedConfigsData.length > 0) {
              for (let i = 0; i < sharedConfigsData.length; i++) {
                sharedConfigsData[i]['lastUsed'] = new Date(
                  sharedConfigsData[i]['lastUsed'] + 'Z',
                ).toLocaleString();
                sharedConfigsData[i]['access_level'] =
                  convertAccessLevelForFrontend(
                    sharedConfigsData[i]['access_level'],
                  );
              }
              // Sort the conversations by last used
              sharedConfigsData.sort((a, b) => {
                return new Date(b.lastUsed) - new Date(a.lastUsed);
              });

              setSharedTemplates(sharedConfigsData);
            }
          })
          .catch((error) => {
            console.log('Error fetching data:', error);
          });
      })
      .catch((error) => {
        console.error('Error fetching user details:', error);
      });
  }, [apiClient, userEmail]);

  useEffect(() => {
    fetchInjector(setHaveInjector);
  }, []);

  function deleteTemplate(configId) {
    setTemplates(
      templates.filter((template) => template.configId !== configId),
    );
    apiClient
      .deleteConfig(configId)
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        // probably no session key
        console.log(error);
        console.log('Did not delete template');
      });
  }
  const deleteTemplateAccess = (configId) => {
    const recipientAccessMapping = {};
    recipientAccessMapping[userEmail] = 'Remove';
    apiClient
      .shareTemplate(configId, recipientAccessMapping)
      .then((response) => {
        // Handle the response if needed
        console.log('Template shared:', response);
        setSharedTemplates(
          sharedtemplates.filter(
            (template) => template.sharedConfigId !== configId,
          ),
        );
      })
      .catch((error) => {
        // Handle errors if API call fails
        console.error('Error sharing template:', error);
        throw error; // Propagate the error to the caller
      });
  };

  const requestDeleteTemplate = (
    configId,
    event,
    isSharedTemplates = false,
  ) => {
    event.stopPropagation(); // Prevent triggering row click
    if (defaultTemplate === configId) {
      setModalOpen(true);
      setModalText(
        'To delete a favorite template, set a different one as favorite first.',
      );
      setModalButtons([
        {
          label: `OK`,
          color: 'primary',
          onClick: () => {},
        },
      ]);
      return;
    }
    setIsModalOpen(true); // Open the modal

    // Set up confirm action with closure capturing the configId
    setConfirmAction(() => () => {
      if (isSharedTemplates) {
        deleteTemplateAccess(configId);
      } else {
        deleteTemplate(configId);
      }
      setIsModalOpen(false); // Close the modal after deletion
    });
  };

  // Function to set a template as default
  const setAsDefaultTemplate = (configId) => {
    // Assuming apiClient has a method to set the default config
    console.log(`Setting new Default Template: ${configId}`);
    apiClient
      .setDefaultConfigId(configId)
      .then(() => {
        console.log(`Set new Default Template: ${configId}`);
        setDefaultTemplate(configId); // Update the default template state
      })
      .catch((error) => {
        console.log('Error setting new default template:', error);
      });
  };

  // Function to create a new template
  function newConfig() {
    apiClient
      .newTemplate()
      .then((data) => {
        navigate(`/User/templates/manager#${data['configId']}`, {
          fromShared: false,
        });
      })
      .catch((error) => {
        console.log(error);
        console.log('Did not create new template');
      });
  }

  const toggleTab = (tab) => {
    setActiveTab(tab);
    setIsEmrTemplates(tab === 'emr');
    setIsSharedTemplates(tab === 'shared');
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalButtons([]); // Reset modalButtons to an empty array
  };

  // Function to render the table for web app templates
  const renderPersonalTemplatesTable = () => {
    // Filter templates based on emr_url condition
    let filteredTemplates = [];
    if (!isEmrTemplates) {
      filteredTemplates = templates.filter(
        (template) =>
          template.emr_url === null ||
          template.emr_url.includes('app.comprehendpt.com'),
      );
    } else {
      filteredTemplates = templates.filter(
        (template) =>
          template.emr_url !== null &&
          !template.emr_url.includes('app.comprehendpt.com'),
      );
    }

    return (
      <div className="templates-container">
        {filteredTemplates.map((template) => (
          <div
            key={template.configId}
            onClick={() =>
              !isCX && template.hasTrainingData && !template.hasQuestions
                ? navigate(`/User/templates/builder#${template.configId}`, {
                    fromShared: false,
                    state: { tab: isEmrTemplates ? 'emr' : 'personal' },
                  })
                : navigate(`/User/templates/manager#${template.configId}`, {
                    fromShared: false,
                    state: { tab: isEmrTemplates ? 'emr' : 'personal' },
                  })
            }
            className={`card clickable-card mb-2 ${
              template.hasTrainingData ? 'bg-secondary' : ''
            }`}
            style={{ padding: '8px' }}
          >
            <div className="card-body d-flex align-items-center p-1">
              <div className="card-buttons mr-2">
                <button
                  className={`btn btn-sm ${
                    template.configId === defaultTemplate
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAsDefaultTemplate(template.configId);
                  }}
                  title="Set as default"
                  style={{ padding: '4px 8px' }}
                >
                  <i
                    className={
                      template.configId === defaultTemplate
                        ? 'fa fa-heart fa-solid' // Solid heart icon
                        : 'fa fa-heart fa-regular' // Regular heart (outlined) icon
                    }
                    style={{
                      color:
                        template.configId === defaultTemplate
                          ? 'white'
                          : 'black', // You can dynamically change the color as well
                    }}
                  ></i>
                </button>
              </div>
              <div
                className="card-content flex-grow-1"
                style={{ minWidth: 0, paddingLeft: '8px' }}
              >
                <h5
                  className="card-title mb-1 text-truncate simple-text"
                  style={{ fontSize: '0.875rem' }}
                  title={`Last Used: ${template.lastUsed}`} // Tooltip with "Last Used" information
                >
                  <TemplateRowName template={template} />
                </h5>
              </div>

              <div className="card-buttons ml-2">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={(e) => requestDeleteTemplate(template.configId, e)}
                  title="Delete"
                  style={{ padding: '4px 8px' }}
                >
                  <i
                    className="fa fa-trash-alt"
                    style={{ color: '#535f7f' }}
                  ></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSharedTemplatesTable = () => (
    <div className="templates-container">
      {sharedtemplates.map((template) => (
        <div
          key={template.sharedConfigId}
          onClick={() =>
            navigate(`/User/templates/manager#${template.sharedConfigId}`, {
              state: { tab: 'shared' },
            })
          }
          className="card clickable-card mb-2"
          style={{ padding: '8px' }} // Reduce card padding
        >
          <div className="card-body d-flex align-items-center p-1">
            <div className="card-buttons mr-2">
              <button
                className={`btn btn-sm ${
                  template.sharedConfigId === defaultTemplate
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setAsDefaultTemplate(template.sharedConfigId);
                }}
                title="Set as default"
                style={{ padding: '4px 8px' }} // Reduce button padding
              >
                <i
                  className={
                    template.sharedConfigId === defaultTemplate
                      ? 'fa fa-heart fa-solid' // Solid heart icon
                      : 'fa fa-heart fa-regular' // Regular heart (outlined) icon
                  }
                  style={{
                    color:
                      template.sharedConfigId === defaultTemplate
                        ? 'white'
                        : 'black', // You can dynamically change the color as well
                  }}
                ></i>
              </button>
            </div>
            <div
              className="card-content flex-grow-1"
              style={{ minWidth: 0, paddingLeft: '8px' }}
            >
              <h5
                className="card-title mb-1 text-truncate"
                style={{ fontSize: '0.875rem' }}
              >
                <SharedTemplateRowName template={template} />
              </h5>
              <p
                className="card-text text-muted"
                style={{ margin: '0', fontSize: '0.75rem' }}
              >
                <i className="fa fa-key mr-1"></i>
                {template.access_level}
                <span className="mx-2">&bull;</span>
                <i className="fa fa-user mr-1"></i>
                {template.sharedConfigOwner}
              </p>
            </div>
            <div className="card-buttons ml-2">
              <button
                className="btn btn-sm btn-secondary"
                onClick={(e) =>
                  requestDeleteTemplate(
                    template.sharedConfigId,
                    e,
                    isSharedTemplates,
                  )
                }
                title="Delete"
                style={{ padding: '4px 8px' }} // Reduce button padding
              >
                <i className="fa fa-trash-alt" style={{ color: '#535f7f' }}></i>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Header isLite={true} />
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        buttons={modalButtons}
      >
        <h3>Warning</h3>
        <p>{modalText}</p>
      </Modal>
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 header-flex">
                <h3 className="mb-0">Note Settings</h3>
              </CardHeader>
              <Nav tabs>
                {isCX && !hideEmrTab ? (
                  <NavItem>
                    <NavLink
                      className={activeTab === 'emr' ? 'active' : ''}
                      onClick={() => toggleTab('emr')}
                    >
                      EMR Note Settings
                    </NavLink>
                  </NavItem>
                ) : null}
                {/* hide this tab if on CX but show if mic drop down*/}
                {!haveInjector ? (
                  <NavItem>
                  <NavLink
                    className={activeTab === 'personal' ? 'active' : ''}
                    onClick={() => toggleTab('personal')}
                  >
                    WebApp Note Settings
                  </NavLink>
                </NavItem>
                ) : null}

                {!isCX && !hideEmrTab ? (
                  <NavItem>
                    <NavLink
                      className={activeTab === 'emr' ? 'active' : ''}
                      onClick={() => toggleTab('emr')}
                    >
                      EMR Note Settings
                    </NavLink>
                  </NavItem>
                ) : null}

                <NavItem>
                  <NavLink
                    className={activeTab === 'shared' ? 'active' : ''}
                    onClick={() => toggleTab('shared')}
                  >
                    Shared Note Settings
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab}>
                {isCX && !hideEmrTab ? (
                  <TabPane tabId="emr">
                    {renderPersonalTemplatesTable()}
                  </TabPane>
                ) : null}

                <TabPane tabId="personal">
                  {renderPersonalTemplatesTable()}
                </TabPane>

                {!isCX && !hideEmrTab ? (
                  <TabPane tabId="emr">
                    {renderPersonalTemplatesTable()}
                  </TabPane>
                ) : (
                  <TabPane></TabPane>
                )}
                <TabPane tabId="shared">{renderSharedTemplatesTable()}</TabPane>
              </TabContent>
            </Card>
          </div>
        </Row>
      </Container>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAction}
      >
        {isSharedTemplates
          ? 'Are you sure you want to remove the access and delete this template?'
          : 'Are you sure you want to delete this template?'}
      </ConfirmationModal>
    </>
  );
};

export default Templates;
